import { useEffect, useRef } from "react";
import * as THREE from "three";

// Tiny 3D wireframe icon. Renders a single rotating geometry in brand blue.
// Used as the icon for each service card. Pauses when offscreen.
const GEOMETRIES = {
  torus: () => new THREE.TorusGeometry(0.9, 0.3, 12, 32),
  capsule: () => new THREE.CapsuleGeometry(0.45, 1, 8, 16),
  icosa: () => new THREE.IcosahedronGeometry(1.05, 0),
  octa: () => new THREE.OctahedronGeometry(1.1, 0),
  knot: () => new THREE.TorusKnotGeometry(0.75, 0.22, 64, 8, 2, 3),
  cube: () => new THREE.BoxGeometry(1.4, 1.4, 1.4),
};

export default function WireframeIcon({ shape = "icosa", size = 96 }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const w = size;
    const h = size;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 50);
    camera.position.z = 4.2;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const geo = (GEOMETRIES[shape] || GEOMETRIES.icosa)();
    const mat = new THREE.MeshBasicMaterial({
      color: 0x29abe2,
      wireframe: true,
      transparent: true,
      opacity: 0.85,
    });
    const mesh = new THREE.Mesh(geo, mat);
    scene.add(mesh);

    // Outer halo
    const halo = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.55, 0),
      new THREE.MeshBasicMaterial({
        color: 0x29abe2,
        wireframe: true,
        transparent: true,
        opacity: 0.12,
      })
    );
    scene.add(halo);

    let visible = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
      },
      { threshold: 0.01 }
    );
    io.observe(mount);

    let raf = 0;
    const clock = new THREE.Clock();
    const animate = () => {
      if (visible) {
        const t = clock.getElapsedTime();
        mesh.rotation.x = t * 0.6;
        mesh.rotation.y = t * 0.9;
        halo.rotation.x = -t * 0.2;
        halo.rotation.y = t * 0.3;
        renderer.render(scene, camera);
      }
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      geo.dispose();
      mat.dispose();
      halo.geometry.dispose();
      halo.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [shape, size]);

  return (
    <div
      ref={mountRef}
      style={{ width: size, height: size }}
      aria-hidden
    />
  );
}
